/**
 * Výjimka s informacemi o neaktuální verzi extensiony a/nebo nativní aplikace
 * @includeToDoc
 */
export class EInsufficientVersionException extends Error {
    public readonly extensionVersionSufficient: boolean;
    public readonly nativeAppVersionSufficient: boolean;
    public readonly requiredExtensionVersion: string;
    public readonly installedExtensionVersion: string;
    public readonly requiredNativeAppVersion: string;
    public readonly installedNativeAppVersion: string;
    public readonly extensionUpdateURL: string;
    public readonly nativeAppUpdateURL: string;

    constructor(extensionVersionSufficient: boolean,
        nativeAppVersionSufficient: boolean,
        requiredExtensionVersion: string,
        installedExtensionVersion: string,
        requiredNativeAppVersion: string,
        installedNativeAppVersion: string,
        extensionUpdateURL: string,
        nativeAppUpdateURL: string) {
        let message = "";
        if (!extensionVersionSufficient)
            message += `Insufficient Crypto extension version. Required version is ${requiredExtensionVersion}. Installed version is ${installedExtensionVersion}.`; //`
        if (!nativeAppVersionSufficient)
            message += `${!extensionVersionSufficient ? "\n" : ""}Insufficient Crypto native app version. Required version is ${requiredNativeAppVersion}. Installed version is ${installedNativeAppVersion}.`; //`
        super(message);
        this.extensionVersionSufficient = extensionVersionSufficient;
        this.nativeAppVersionSufficient = nativeAppVersionSufficient;
        this.requiredExtensionVersion = requiredExtensionVersion;
        this.installedExtensionVersion = installedExtensionVersion;
        this.requiredNativeAppVersion = requiredNativeAppVersion;
        this.installedNativeAppVersion = installedNativeAppVersion;
        this.extensionUpdateURL = extensionUpdateURL;
        this.nativeAppUpdateURL = nativeAppUpdateURL;

        Object.setPrototypeOf(this, EInsufficientVersionException.prototype); // ES5 pro LW
    }

    public override toString(): string {
        return this.message;
    }
}

/**
 * Obecná výjimka podepisovací aplikace
 * @includeToDoc
 */
export class NativeAppException extends Error {
    constructor(message?: string) {
        super(message);

        Object.setPrototypeOf(this, NativeAppException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka oznamující, že není dostupná nativní podepisovací aplikace
 * @includeToDoc
 */
export class NativeAppUnavailableException extends NativeAppException {
    public downloadURL: string | undefined;

    constructor(downloadURL?: string) {
        super("Native application unavailable");
        this.downloadURL = downloadURL;

        Object.setPrototypeOf(this, NativeAppUnavailableException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka oznamující, že není dostupné rozšíření pro podepisování
 * @includeToDoc
 */
export class CryptoExtensionUnavailableException extends Error {
    public downloadURL: string | undefined;

    constructor(downloadURL?: string) {
        super("Crypto extension unavailable");
        this.downloadURL = downloadURL;

        Object.setPrototypeOf(this, CryptoExtensionUnavailableException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka oznamující, že prohlížeč (IE) běží v chráněném režimu a v tom nejde pracovat s CryptoNativeApp.
 * @includeToDoc
 */
export class ProtectedModeException extends NativeAppException {
    constructor() {
        super();

        Object.setPrototypeOf(this, ProtectedModeException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka oznamující, že podepisovací aplikace byla ukončena
 * @includeToDoc
 */
export class NativeAppExitedException extends NativeAppException {
    constructor() {
        super();

        Object.setPrototypeOf(this, NativeAppExitedException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka vyvolaná při nevyplněném selectionParams
 * @includeToDoc
 */
export class NativeAppSelectionParamsException extends NativeAppException {
    constructor() {
        super();

        Object.setPrototypeOf(this, NativeAppSelectionParamsException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka vyvolaná pokud je specifikováno selectionParams a certifikát nebyl nalezen
 * @includeToDoc
 */
export class NativeAppNoCertificateFoundException extends NativeAppException {
    constructor() {
        super();

        Object.setPrototypeOf(this, NativeAppNoCertificateFoundException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka vyvolaná pokud je specifikováno selectionParams a certifikát nebyl jedinečný
 * @includeToDoc
 */
export class NativeAppTooManyCertificatesFoundException extends NativeAppException {
    constructor() {
        super();

        Object.setPrototypeOf(this, NativeAppTooManyCertificatesFoundException.prototype); // ES5 pro LW
    }
}

/**
 * Výjimka vyvolaná při podepisování streamu
 * @includeToDoc
 */
export class NativeAppSigningException extends NativeAppException {
    public override readonly name: string;
    public readonly streamId: string;
    constructor(streamId: string, name: string, err: string) {
        super(err);
        this.name = name;
        this.streamId = streamId;
        Object.setPrototypeOf(this, NativeAppSigningException.prototype); // ES5 pro LW
    }
}

/**
 * Kolekce výjimek, které byly vyvolány během hromadné operace.
 * Výjimky jsou organizovány formou dictionary, kde klíčem je objekt, který výjimku vyvolal a k tomuto objektu se eviduje pole výjimek, které vyvolal.
 * @includeToDoc
 */
export class NativeAppBulkException extends NativeAppException {
    private _exceptions: NativeAppSigningException[];

    public get hasAnyError(): boolean {
        return this._exceptions && this._exceptions.length > 0;
    }

    constructor() {
        super();
        this._exceptions = [];

        Object.setPrototypeOf(this, NativeAppBulkException.prototype); // ES5 pro LW
    }

    public addError(err: NativeAppSigningException): void {
        this._exceptions.push(err);
    }

    public [Symbol.iterator](): Iterator<NativeAppSigningException> {
        return this._exceptions[Symbol.iterator]();
    }
}
